import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { RefreshCw, CalendarDays, Archive, Radio } from "lucide-react";
import { toast } from "sonner";
import { NavBar } from "@/components/NavBar";
import { FiveDCard, SixDCard } from "@/components/Lotto56Card";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const todayMYT = () => {
  const now = new Date();
  const myt = new Date(now.getTime() + now.getTimezoneOffset() * 60000 + 8 * 3600000);
  return myt.toISOString().slice(0, 10);
};

const Skeleton = () => (
  <div className="animate-pulse overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
    <div className="h-14 bg-slate-200" />
    <div className="space-y-3 p-4">
      {Array.from({ length: 5 }).map((_, i) => <div key={i} className="h-9 rounded-lg bg-slate-100" />)}
    </div>
  </div>
);

export default function Lotto56() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedDate, setSelectedDate] = useState("");
  const today = todayMYT();
  const isLive = selectedDate === "" || selectedDate === today;

  const load = useCallback(async (dateArg, force = false, showToast = false) => {
    if (force) setRefreshing(true);
    const live = dateArg === "" || dateArg === today;
    try {
      const params = [];
      if (!live) params.push(`date=${dateArg}`);
      if (force) params.push("force=true");
      const res = await axios.get(`${API}/results56${params.length ? "?" + params.join("&") : ""}`);
      setData(res.data);
      if (showToast) toast.success("Result 5D/6D dikemaskini");
    } catch (e) {
      if (showToast) toast.error("Gagal muat result.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [today]);

  useEffect(() => { load(selectedDate); }, [selectedDate, load]);
  useEffect(() => {
    if (!isLive) return;
    const interval = setInterval(() => load(selectedDate), 120000);
    return () => clearInterval(interval);
  }, [isLive, selectedDate, load]);

  const fiveD = (data?.fiveD || []).filter((r) => r.available);
  const sixD = (data?.sixD || []).filter((r) => r.available);

  return (
    <div className="min-h-screen bg-slate-50 pb-20">
      <NavBar />

      <div className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-[1500px] flex-wrap items-center gap-2.5 px-4 py-4 sm:px-6">
          <div className="flex flex-1 items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 sm:flex-none">
            <CalendarDays className="h-4 w-4 shrink-0 text-slate-400" />
            <input data-testid="date-picker-56" type="date" max={today} value={selectedDate || today}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="w-full bg-transparent text-sm font-medium text-slate-700 outline-none sm:w-auto" />
          </div>
          {!isLive && (
            <button data-testid="live-reset-56" onClick={() => setSelectedDate("")}
              className="flex items-center gap-1.5 rounded-full bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700">
              <Radio className="h-4 w-4" /> Terkini
            </button>
          )}
          {!isLive && (
            <div className="flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-700">
              <Archive className="h-3.5 w-3.5" /> Mod Arkib
            </div>
          )}
          <button data-testid="refresh-56" onClick={() => load(selectedDate, true, true)} disabled={refreshing}
            className="flex flex-1 items-center justify-center gap-2 rounded-full bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-700 disabled:opacity-60 sm:ml-auto sm:flex-none sm:py-2">
            <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
      </div>

      <main className="mx-auto max-w-[1500px] px-4 pt-6 sm:px-6">
        {loading ? (
          <div className="grid auto-rows-fr grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
            {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} />)}
          </div>
        ) : (
          <>
            <h2 className="mb-4 font-display text-xl font-black text-slate-900">Result 5D</h2>
            {fiveD.length ? (
              <div className="mb-10 grid auto-rows-fr grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
                {fiveD.map((r, i) => <FiveDCard key={r.provider} result={r} index={i} />)}
              </div>
            ) : (
              <p className="mb-10 text-sm text-slate-500" data-testid="no-5d">Tiada cabutan 5D pada tarikh ini.</p>
            )}

            <h2 className="mb-4 font-display text-xl font-black text-slate-900">Result 6D</h2>
            {sixD.length ? (
              <div className="grid auto-rows-fr grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
                {sixD.map((r, i) => <SixDCard key={r.provider} result={r} index={i} />)}
              </div>
            ) : (
              <p className="text-sm text-slate-500" data-testid="no-6d">Tiada cabutan 6D pada tarikh ini.</p>
            )}
          </>
        )}

        <footer className="mt-10 border-t border-slate-200 pt-6 text-center text-xs text-slate-400">
          <p>Sumber: 4dyes (Toto, Perdana, Good4D, 9 Lotto, 4DYes). Result adalah rujukan sahaja.</p>
        </footer>
      </main>
    </div>
  );
}
